import { useMemo, useState } from "react";

import Box from "@/components/Box";
import Text from "@/components/Text";
import PartnerDashboardBox from "@/components/boxes/PartnerDashboardBox";
import useListControls from "@/hooks/useListControls";
import SortMenu from "@/tpo/SortMenu";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";
import { useMutation, useQuery } from "@apollo/client";
import { useTheme } from "styled-components";

export function getAnswerColor(score) {
  if (score === 0) return "green";
  if (score === 1) return "orange";
  return "red";
}

export const QUESTION_SORT_OPTIONS = [
  { label: "High to Low", value: "high_to_low" },
  { label: "Low to High", value: "low_to_high" }
];

export function useSortedQuestions(questions, sortValue) {
  return useMemo(() => {
    if (sortValue === "high_to_low") {
      return [...questions].sort(
        (a, b) => b.answerScore - a.answerScore || questions.indexOf(a) - questions.indexOf(b)
      );
    }
    if (sortValue === "low_to_high") {
      return [...questions].sort(
        (a, b) => a.answerScore - b.answerScore || questions.indexOf(a) - questions.indexOf(b)
      );
    }
    return questions;
  }, [questions, sortValue]);
}

export function QuestionList({ questions }) {
  return (
    <Box>
      {questions.map(question => (
        <Box
          key={question.id}
          display="flex"
          flexDirection={["column", "column", "row"]}
          gap={[10, 10, 0]}
          py={["10px", "10px", "20px"]}
          px={["20px", "20px", "28px"]}
          borderBottom="1px solid"
          borderColor="haze"
        >
          <Box textAlign="left" width={[null, null, 0.6]} fontSize={[14]}>
            {question.question.text}
          </Box>
          <Box textAlign={["left", "left", "right"]} width={[null, null, 0.4]}>
            <Text
              color={question.answerText ? getAnswerColor(question.answerScore) : "midGrey"}
              fontFamily="gilroyBold"
              fontSize={[14, 14, 16]}
            >
              {question.answerText || "No Answer"}
            </Text>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export function AdditionalInfo({
  title = "Additional information",
  titleProps,
  children,
  ...boxProps
}) {
  return (
    <Box bg="haze" p={28} pb={48} borderRadius={20} {...boxProps}>
      <Box fontFamily="gilroyBold" fontSize={[24]} {...titleProps}>
        {title}
      </Box>
      <Box fontFamily="gilroyMedium" fontSize={[16]}>
        {children}
      </Box>
    </Box>
  );
}

export function useQuestionnaireReport(query, { createSubmissionMutation, userId, ...queryOptions } = {}) {
  const [showReport, setShowReport] = useState(false);
  const [forceQuestionnaire, setForceQuestionnaire] = useState(false);
  const { data, loading, refetch } = useQuery(query, queryOptions);
  const [createUserSubmission] = useMutation(createSubmissionMutation);

  const report = data?.userQuestionnaireReport;
  const hasReport = report?.hasAnswers;

  const handleContinue = async () => {
    await refetch();
    setForceQuestionnaire(false);
    setShowReport(true);
  };

  const handleRetake = async () => {
    await createUserSubmission({
      variables: { input: { userId } }
    });
    setForceQuestionnaire(true);
    setShowReport(false);
  };

  const showReportView = !loading && !forceQuestionnaire && (showReport || hasReport);

  return {
    hasReport,
    report,
    loading,
    showReportView,
    handleContinue,
    handleRetake
  };
}

export default function QuestionnaireReport({ report, onRetake, description }) {
  const theme = useTheme();
  const questions = report?.userQuestions || [];

  return (
    <Box bg="haze" px={20} pt={theme.spacing.section.pt} pb={theme.spacing.section.pb}>
      <Stack maxWidth={1532} mx="auto" gap={40}>
        <PartnerDashboardBox
          pt={[60]}
          pb={[80]}
          borderRadius={20}
          maxWidth={1024}
          innerMaxWidth={944}
        >
          <Box display="flex" flexDirection="column" gap={40}>
            <Stack gap={16} maxWidth={720}>
              <Box
                fontFamily="gilroyBold"
                fontSize={[28, 28, 36]}
                textAlign="left"
                lineHeight="46px"
              >
                Self assessment
              </Box>
              <Box
                fontFamily="gilroyMedium"
                fontSize={[14, 14, 16]}
                lineHeight="28px"
                textAlign="left"
              >
                {description || (
                  <>
                    Below are your latest responses to the symptoms questionnaire, which your
                    practitioner can review ahead of your consultations.
                    <br />
                    Keeping this information up to date every 3–6 months helps ensure you receive
                    the most relevant guidance and support.
                  </>
                )}
              </Box>
            </Stack>
            <Stack gap={10} alignItems="flex-start">
              <Box fontFamily="gilroyBold" fontSize={[14]}>
                Latest submission:{" "}
                {report.submittedAt
                  ? new Date(report.submittedAt).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric"
                    }) // format 02 Feb 2026
                  : "No submission"}
              </Box>
              {onRetake && (
                <ButtonV2 color="dark" onClick={onRetake} size={["sm", "sm", "md"]}>
                  retake questionnaire
                </ButtonV2>
              )}
            </Stack>
            {report.additionalInformation && (
              <AdditionalInfo>{report.additionalInformation}</AdditionalInfo>
            )}
            <QuestionList questions={questions} />
          </Box>
        </PartnerDashboardBox>
      </Stack>
    </Box>
  );
}
